import {noop} from '@benchling/js-utils';
import React from 'react';

import {assert} from 'Asserts';
import {MinimalFolder} from 'constants/FolderConstants';
import {REGISTRY_TYPE} from 'constants/SourceConstants';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import FolderDAO from 'file-browser/FolderDAO';
import FolderSelectTree from 'file-browser/FolderSelectTree';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import LegacyTagsInput from 'ui-library/tags-input/LegacyTagsInput';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';

export interface MultipleFolderSelectorProps {
  allFolders?: Array<MinimalFolder>;
  dataTestAttrs?: DataTestAttrs;
  includeHiddenProjects: boolean;
  onLoadAllFolders?: (folders: Array<MinimalFolder>) => void;
  onSelectFolders: (folders: Array<MinimalFolder>) => void;
  selectedFolderIds: Array<string>;
  showInventory: boolean;
  showNotebook: boolean;
  showRegistries: boolean;
  placeHolderText: string;
  tagsInputSize: TagsInputSize;
}

export interface MultipleFolderSelectorState {
  folders: Array<MinimalFolder> | null;
  open: boolean;
}

export default class MultipleFolderSelector extends React.Component<
  MultipleFolderSelectorProps,
  MultipleFolderSelectorState
> {
  static defaultProps = {
    includeHiddenProjects: false,
    showInventory: false,
    showNotebook: false,
    showRegistries: true,
    placeHolderText: 'Add locations',
    tagsInputSize: TagsInputSize.LARGE,
  };

  _isMounted: boolean = false;

  override state: MultipleFolderSelectorState = {
    folders: null,
    open: false,
  };

  override componentDidMount(): void {
    this._isMounted = true;
    FolderDAO.fetchAll({level: 'read'})
      .done((folders) => {
        if (this._isMounted) {
          this.setAllFolders(folders!);
        }
      })
      .fail(notifications.notifyError);
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
  }

  override componentDidUpdate(prevProps: MultipleFolderSelectorProps): void {
    if (
      this.props.showInventory !== prevProps.showInventory ||
      this.props.showNotebook !== prevProps.showNotebook
    ) {
      // TODO(T4346): The child component shouldn't be updating the parent on componentDidUpdate
      assert(this.getAllFolders() != null, 'Switched to projectsOnly before folders were loaded');
      // Switching to project-only, so filter to only projects.
      const predicate = FileBrowserUtil.propsToFolderPredicate(this.props);
      const folders = this.props.selectedFolderIds
        .map((folderId) => this.getFolder(folderId))
        .filter((folder): folder is MinimalFolder => folder != null && predicate(folder));
      this.props.onSelectFolders(folders);
    }
  }

  setAllFolders = (folders: Array<MinimalFolder>): void => {
    let foldersToSet = folders;
    if (!this.props.showRegistries) {
      foldersToSet = folders.filter((folder: MinimalFolder) => folder.source?.sourceType !== REGISTRY_TYPE);
    }
    if (this.props.onLoadAllFolders != null) {
      this.props.onLoadAllFolders(foldersToSet);
    }

    this.setState({folders: foldersToSet});

    // Remove any folders from the selected folders if they no longer exist.
    const folderIdLookup = Object.fromEntries(folders.map((folder) => [folder.api_identifier, folder]));
    const newFolders = this.props.selectedFolderIds
      .filter((id) => folderIdLookup.hasOwnProperty(id))
      .map((id) => folderIdLookup[id]);

    if (newFolders.length < this.props.selectedFolderIds.length) {
      this.props.onSelectFolders(newFolders);
    }
  };

  private getAllFolders = (): Array<MinimalFolder> | null => {
    if (this.props.allFolders) {
      return this.props.allFolders;
    } else {
      return this.state.folders;
    }
  };

  private getFolder = (folderId: string): MinimalFolder | undefined | null => {
    const folders = this.getAllFolders();
    if (folders == null) {
      return null;
    }
    return folders.find((folder) => folder.api_identifier === folderId);
  };

  private updateSelectedFolders = (newFolderIds: Array<string>): void => {
    if (this.getAllFolders() == null) {
      // Don't do anything if folders haven't loaded yet
      return;
    }
    const selectedFolders: Array<MinimalFolder> = newFolderIds
      .map((folderId) => this.getFolder(folderId))
      .filter((folder): folder is MinimalFolder => folder != null);
    this.props.onSelectFolders(selectedFolders);
  };

  private toggleOpen = (): void => {
    this.setState({open: !this.state.open});
  };

  private handleBackdropMouseDown = (): void => {
    // Close the dropdown on outside click.
    this.setState({open: false});
  };

  private getFolderName = (folderId: string): string => {
    const folder = this.getFolder(folderId);
    return folder ? folder.name : 'Loading...';
  };

  private isWaitingForLoadingFolders = (): boolean => this.getAllFolders() == null;

  private renderDropdown = (): JSX.Element => {
    if (this.isWaitingForLoadingFolders()) {
      return <div className="faded u-tiny-padding">Loading...</div>;
    }
    const folders = this.getAllFolders();
    const predicate = FileBrowserUtil.propsToFolderPredicate(this.props);
    return (
      <FolderSelectTree
        folderIds={this.props.selectedFolderIds}
        onChangeSelectedFolders={this.updateSelectedFolders}
        folders={folders!.filter((folder) => predicate(folder))}
        multiple={true}
        submitHandler={noop}
        includeHiddenProjects={this.props.includeHiddenProjects}
      />
    );
  };

  override render(): JSX.Element {
    return (
      <div className="multipleFolderSelector" {...getTestProps(this.props.dataTestAttrs)}>
        {this.state.open && <div className="backdrop" onMouseDown={this.handleBackdropMouseDown} />}
        {this.state.open && <div className="multipleFolderSelector-dropdown">{this.renderDropdown()}</div>}
        <div className="u-pointer" onClick={this.toggleOpen}>
          <LegacyTagsInput
            allowAdding={false}
            allowSearching={false}
            inputClass="u-pointer"
            value={this.props.selectedFolderIds.map((id) => ({text: this.getFolderName(id), id}))}
            onChange={(options: Array<{id: string}>) => {
              this.updateSelectedFolders(options.map(({id}) => id));
            }}
            placeholder={this.isWaitingForLoadingFolders() ? 'Loading...' : this.props.placeHolderText}
            size={this.props.tagsInputSize}
            dataTestAttrs={{component: 'MultipleFolderSelector', element: 'root'}}
          />
        </div>
      </div>
    );
  }
}
